'use client';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { Divider } from '../divider/Divider';
import { useListContext } from './ListContext';

const listDividerClasses = { root: 'zds-list-divider-root' } as const;

const listDividerStyles = tv({
  base: [listDividerClasses.root],
  variants: {
    density: {
      compact: 'my-0.5',
      default: 'my-1',
      spacious: 'my-1.5',
    },
    densityXs: {
      compact: 'xs:my-0.5',
      default: 'xs:my-1',
      spacious: 'xs:my-1.5',
    },
    densitySm: {
      compact: 'sm:my-0.5',
      default: 'sm:my-1',
      spacious: 'sm:my-1.5',
    },
    densityMd: {
      compact: 'md:my-0.5',
      default: 'md:my-1',
      spacious: 'md:my-1.5',
    },
    densityLg: {
      compact: 'lg:my-0.5',
      default: 'lg:my-1',
      spacious: 'lg:my-1.5',
    },
    densityXl: {
      compact: 'xl:my-0.5',
      default: 'xl:my-1',
      spacious: 'xl:my-1.5',
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ListDividerBaseProps {}

type ListDividerProps = ComponentProps<ListDividerBaseProps, 'li'>;

type ListDividerComponent = Component<ListDividerBaseProps, 'li'>;

const ListDivider: ListDividerComponent = (props) => {
  const { className, ref, ...rest } = props;

  const context = useListContext();
  const density = context?.density ?? 'default';
  const responsiveDensity = context?.responsiveDensity;

  return (
    <Divider
      {...rest}
      component="li"
      isDecorative
      ref={ref}
      className={listDividerStyles({
        density,
        densityXs: responsiveDensity?.xs,
        densitySm: responsiveDensity?.sm,
        densityMd: responsiveDensity?.md,
        densityLg: responsiveDensity?.lg,
        densityXl: responsiveDensity?.xl,
        className,
      })}
    />
  );
};

ListDivider.displayName = 'List.Divider';

export {
  ListDivider,
  type ListDividerProps,
  type ListDividerBaseProps,
  listDividerClasses,
};
